/**
 * The founder dashboard (BRIEF surface 5): workspaces, runs, token cost,
 * the rulebase verification queue — plus the retention controls, because a
 * deletion job nobody can see is a deletion job nobody trusts.
 *
 * Velox-internal, and it says so. The server refuses every route below with 403
 * for a member; this screen additionally never renders for one, so a prospect
 * who guesses the URL gets a sentence rather than five failed requests.
 *
 * Tables, not cards: this is a comparison surface — "which Büro has run what,
 * and what did it cost" — and a table is the accessible way to compare. Every
 * table has a caption and column headers with `scope="col"`; numbers are in the
 * mono face; nothing is signalled by colour alone.
 */
import { useState } from 'react'
import { ShieldCheck, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { LoadingBlock } from '@/components/LoadingBlock'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AuditTable } from '@/features/audit/AuditTable'
import { formatMoment } from '@/features/audit/auditFormat'
import { useSession } from '@/features/auth/SessionContext'
import { formatDate } from '@/features/retention/retention'
import { useI18n } from '@/i18n'
import type { TranslationKey } from '@/i18n'
import { Link, buildPath } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'
import { formatNumber } from '@/lib/utils'
import type {
  AdminOverview,
  AdminRunRow,
  AuditEventRef,
  RetentionOverview,
  RetentionSweepResult,
  VerificationQueueItem,
} from '@/api/types'

const MB = 1024 * 1024

function megabytes(bytes: number): string {
  return `${formatNumber(Math.round((bytes / MB) * 10) / 10)} MB`
}

export function AdminScreen() {
  const { t } = useI18n()
  const { isFounder } = useSession()
  const overview = useAsync<AdminOverview>((signal) => api.adminOverview(signal), [isFounder])
  const runs = useAsync<AdminRunRow[]>((signal) => api.adminRuns(25, signal), [isFounder])
  const queue = useAsync<VerificationQueueItem[]>(
    (signal) => api.adminVerificationQueue(signal),
    [isFounder],
  )
  const system = useAsync<AuditEventRef[]>((signal) => api.adminSystemAudit(50, signal), [isFounder])

  // Four independent requests, four sections, and each one used to appear on
  // its own — so the dashboard rearranged itself three times while the founder
  // was already reading it (CLS 0.145, the worst surface left once the report
  // was fixed). Reserving a height per section would be four guesses at row
  // counts that vary by workspace; waiting once for all four is one honest
  // state change. A section that errors resolves too, so a single broken
  // endpoint cannot hold the page hostage — it lands with its own ErrorNote.
  //
  // The latch matters: `reload()` after a sweep or a deletion puts a source
  // back into `loading`, and gating on the live flags would blank the whole
  // dashboard — together with the result the founder just asked for. Only the
  // *first* paint waits.
  const settled = !overview.loading && !runs.loading && !queue.loading && !system.loading
  const [everSettled, setEverSettled] = useState(false)
  // Adjusting state during render, not in an effect: React re-runs the render
  // immediately and nothing downstream ever sees the stale value.
  if (settled && !everSettled) setEverSettled(true)

  if (!isFounder) {
    return (
      <p role="alert" className="text-[15px] text-ink" data-testid="admin-forbidden">
        {t('admin.forbidden')}
      </p>
    )
  }

  if (!settled && !everSettled) return <LoadingBlock />

  return (
    <div className="flex flex-col gap-12">
      <section className="flex flex-col gap-3">
        <h1 className="font-display text-[32px] leading-tight font-semibold">{t('admin.title')}</h1>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('admin.lead')}</p>
      </section>

      {overview.error ? (
        <ErrorNote message={overview.error} onRetry={overview.reload} retryLabel={t('common.retry')} />
      ) : null}

      <WorkspaceTable overview={overview.data} />
      <RetentionPanel onChanged={() => {
        overview.reload()
        system.reload()
      }} />
      <VerificationQueue items={queue.data} error={queue.error} onRetry={queue.reload} />
      <RunTable rows={runs.data} error={runs.error} onRetry={runs.reload} />

      <section aria-labelledby="admin-system" className="flex flex-col gap-4">
        <div>
          <h2 id="admin-system" className="font-display text-[21px] font-semibold">
            {t('admin.systemTitle')}
          </h2>
          <p className="mt-1 text-[13px] text-muted">{t('admin.systemLead')}</p>
        </div>
        {system.error ? (
          <ErrorNote message={system.error} onRetry={system.reload} retryLabel={t('common.retry')} />
        ) : null}
        <AuditTable events={system.data ?? []} caption={t('admin.systemTitle')} />
      </section>
    </div>
  )
}

// --------------------------------------------------------------------------
// Workspaces
// --------------------------------------------------------------------------
function WorkspaceTable({ overview }: { overview: AdminOverview | null }) {
  const { t } = useI18n()
  const rows = overview?.workspaces ?? []
  return (
    <section aria-labelledby="admin-workspaces" className="flex flex-col gap-4">
      <div>
        <h2 id="admin-workspaces" className="font-display text-[21px] font-semibold">
          {t('admin.workspacesTitle')}
        </h2>
        <p className="mt-1 text-[13px] text-muted">{t('admin.workspacesCaption')}</p>
        <p className="mt-1 max-w-[70ch] text-[13px] text-muted">{t('admin.dayBudgetLead')}</p>
      </div>
      {/* Keyboard access to the horizontal scroll (WCAG 2.2 §2.1.1) — the
          table's min-width overflows at 320 CSS px and a scroll container with
          no focus stop cannot be moved without a mouse. `group`, not `region`:
          the <section> above is already a landmark with this name, and two
          landmarks called the same thing are worse than one. */}
      <div className="overflow-x-auto" tabIndex={0} role="group" aria-label={t('admin.workspacesCaption')}>
        <table
          className="w-full min-w-[1040px] border-collapse text-left text-[13px]"
          data-testid="admin-workspaces"
        >
          <caption className="sr-only">{t('admin.workspacesCaption')}</caption>
          <thead>
            <tr className="border-b border-border text-[12px] tracking-wide text-muted uppercase">
              <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.workspace')}</th>
              <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.dataMode')}</th>
              <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.members')}</th>
              <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.dossiers')}</th>
              <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.runs')}</th>
              <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.storage')}</th>
              <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.drafts')}</th>
              <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.tokens')}</th>
              <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.tokensDay')}</th>
              <th scope="col" className="py-2 font-medium">{t('admin.col.retention')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-border align-top">
                <th scope="row" className="py-2 pe-4 text-left font-medium text-ink">
                  <Link
                    to={buildPath('workspace', { workspaceId: row.id })}
                    className="underline underline-offset-2"
                  >
                    {row.name}
                  </Link>
                </th>
                <td className="py-2 pe-4 text-muted">{row.data_mode}</td>
                <td className="num py-2 pe-4">{formatNumber(row.members)}</td>
                <td className="num py-2 pe-4">{formatNumber(row.dossiers)}</td>
                <td className="num py-2 pe-4">
                  {formatNumber(row.runs)}
                  {row.runs_failed > 0 ? (
                    <span className="ms-2 text-[12px] text-signal-red-text">
                      {formatNumber(row.runs_failed)} {t('admin.col.failed')}
                    </span>
                  ) : null}
                </td>
                <td className="num py-2 pe-4 whitespace-nowrap">{megabytes(row.bytes)}</td>
                <td className="num py-2 pe-4">{formatNumber(row.draft_packs)}</td>
                {/* Cost is founder-internal. The object is always present on
                    this route, and a zero is a measurement — but a missing
                    object still renders nothing rather than a zero. */}
                <td className="num py-2 pe-4 whitespace-nowrap" data-testid="admin-cost">
                  {row.cost
                    ? `${formatNumber(row.cost.input_tokens)} / ${formatNumber(row.cost.output_tokens)}`
                    : null}
                </td>
                {/* The rolling 24-hour spend next to the ceiling it is measured
                    against (BAUPILOT_TOKEN_BUDGET_PER_WORKSPACE_DAY): a number
                    without its limit does not tell the founder whether it is
                    large. A ceiling of 0 means "switched off", and the cell
                    says so with a dash — a printed 0 would read as "nothing
                    allowed". The marker is the server's one boolean, shown as
                    words: no threshold is invented here, and nothing is said
                    by colour alone. */}
                <td className="num py-2 pe-4" data-testid="admin-tokens-day">
                  <span className="whitespace-nowrap">
                    {formatNumber(row.tokens_last_24h)} /{' '}
                    {row.day_budget_tokens > 0 ? formatNumber(row.day_budget_tokens) : '—'}
                  </span>
                  {row.day_budget_exceeded ? (
                    <span role="status" className="block font-medium text-signal-red-text">
                      {t('admin.dayBudgetReached')}
                    </span>
                  ) : null}
                </td>
                <td className="py-2 whitespace-nowrap text-muted">
                  {t('retention.window', { days: row.retention_days })}
                  {row.dossiers_due > 0 ? (
                    <span className="ms-2 font-medium text-ink">
                      · {formatNumber(row.dossiers_due)} {t('admin.col.due')}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
            {overview ? (
              <tr className="border-t-2 border-border-strong font-medium">
                <th scope="row" className="py-2 pe-4 text-left">{t('admin.totals')}</th>
                <td className="py-2 pe-4" />
                <td className="py-2 pe-4" />
                <td className="num py-2 pe-4">{formatNumber(overview.totals.dossiers)}</td>
                <td className="num py-2 pe-4">{formatNumber(overview.totals.runs)}</td>
                <td className="num py-2 pe-4 whitespace-nowrap">{megabytes(overview.totals.bytes)}</td>
                <td className="num py-2 pe-4">{formatNumber(overview.totals.draft_packs)}</td>
                <td className="num py-2 pe-4 whitespace-nowrap">
                  {overview.totals.cost
                    ? `${formatNumber(overview.totals.cost.input_tokens)} / ${formatNumber(overview.totals.cost.output_tokens)}`
                    : null}
                </td>
                {/* The sum alone. The ceiling is per workspace; the totals row
                    has none, and pairing the sum with one would invent it. */}
                <td className="num py-2 pe-4 whitespace-nowrap" data-testid="admin-tokens-day-total">
                  {formatNumber(overview.totals.tokens_last_24h)}
                </td>
                <td className="py-2" />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// --------------------------------------------------------------------------
// Retention
// --------------------------------------------------------------------------
export function RetentionPanel({ onChanged }: { onChanged: () => void }) {
  const { t } = useI18n()
  const state = useAsync<RetentionOverview>((signal) => api.adminRetention(signal), [])
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<RetentionSweepResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sweep = async (dryRun: boolean) => {
    setBusy(true)
    setError(null)
    try {
      setResult(await api.adminSweepRetention(dryRun))
      state.reload()
      onChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const data = state.data
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.retentionTitle')}</CardTitle>
        <CardDescription>{t('retention.note')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {state.error ? (
          <ErrorNote message={state.error} onRetry={state.reload} retryLabel={t('common.retry')} />
        ) : null}
        {data ? (
          <>
            <p className="text-[13px] text-muted" data-testid="retention-schedule">
              {data.sweep_enabled
                ? t('admin.sweepEvery', { minutes: Math.round(data.interval_seconds / 60) })
                : t('admin.sweepOff')}
            </p>
            <p className="text-[13px] text-muted">
              {data.last_sweep_at
                ? t('admin.lastSweep', {
                    when: formatMoment(data.last_sweep_at),
                    count: data.last_sweep_deleted ?? 0,
                  })
                : t('admin.neverSwept')}
            </p>
            <p className="text-[15px] text-ink" data-testid="retention-due">
              {data.due_now > 0 ? t('admin.dueNow', { count: data.due_now }) : t('admin.dueNone')}
            </p>
            <ul className="flex flex-col gap-1 text-[13px] text-muted">
              {(data.workspaces ?? []).map((row) => (
                <li key={row.workspace_id}>
                  {row.name}: {t('retention.window', { days: row.retention_days })} ·{' '}
                  {formatNumber(row.dossiers)} {t('admin.col.dossiers')}
                  {row.next_deletion_at
                    ? ` · ${t('retention.deleteOn', { date: formatDate(row.next_deletion_at) })}`
                    : ''}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {error ? <ErrorNote message={error} /> : null}
        {result ? (
          <p role="status" className="text-[13px] text-ink" data-testid="sweep-result">
            {result.dry_run
              ? t('admin.sweepDryDone', { due: result.due })
              : t('admin.sweepDone', {
                  dossiers: result.dossiers,
                  files: result.files,
                  runs: result.runs,
                })}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => void sweep(true)}>
            {t('admin.sweepDry')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy}
            onClick={() => void sweep(false)}
            data-testid="sweep-now"
          >
            <Icon icon={Trash2} />
            {busy ? t('admin.sweepBusy') : t('admin.sweepRun')}
          </Button>
        </div>

        <DeleteWorkspacePanel onDeleted={onChanged} />
      </CardContent>
    </Card>
  )
}

/**
 * Workspace deletion: the destructive act with the largest blast radius in the
 * product, so it asks for the workspace's short name to be typed. That is not
 * decoration — the server compares `confirm` against the slug and refuses with
 * 422 otherwise, so the same question is asked of a script.
 */
function DeleteWorkspacePanel({ onDeleted }: { onDeleted: () => void }) {
  const { t } = useI18n()
  const { workspaces, refresh } = useSession()
  const [selected, setSelected] = useState('')
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const workspace = workspaces.find((candidate) => candidate.id === selected) ?? null
  const matches = workspace !== null && typed.trim() === workspace.slug

  const remove = async () => {
    if (!workspace || !matches) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.deleteWorkspace(workspace.id, typed.trim())
      setDone(
        t('admin.deleteWorkspaceDone', {
          dossiers: result.dossiers,
          files: result.files,
          runs: result.runs,
          audit: result.audit_deleted,
        }),
      )
      setSelected('')
      setTyped('')
      await refresh()
      onDeleted()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section
      aria-labelledby="admin-delete-workspace"
      className="mt-4 flex flex-col gap-3 rounded-sm border border-border-strong bg-surface px-4 py-4"
    >
      <h3 id="admin-delete-workspace" className="text-[15px] font-medium text-ink">
        {t('admin.deleteWorkspaceTitle')}
      </h3>
      <p className="text-[13px] text-muted">{t('admin.deleteWorkspaceWhat')}</p>
      <p className="text-[13px] font-medium text-signal-red-text">{t('delete.irreversible')}</p>
      <p className="text-[13px] text-muted">{t('admin.deleteWorkspaceKeeps')}</p>

      <label className="flex flex-col gap-1 text-[13px] text-ink-2" htmlFor="admin-ws-select">
        {t('admin.selectWorkspace')}
        <select
          id="admin-ws-select"
          className="h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink"
          value={selected}
          onChange={(event) => {
            setSelected(event.target.value)
            setTyped('')
            setDone(null)
          }}
        >
          <option value="">—</option>
          {workspaces.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name} ({candidate.slug})
            </option>
          ))}
        </select>
      </label>

      {workspace ? (
        <label className="flex flex-col gap-1 text-[13px] text-ink-2" htmlFor="admin-ws-confirm">
          {t('admin.deleteWorkspaceLabel', { slug: workspace.slug })}
          <input
            id="admin-ws-confirm"
            className="h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink"
            value={typed}
            autoComplete="off"
            onChange={(event) => setTyped(event.target.value)}
            aria-describedby="admin-ws-confirm-hint"
          />
          <span id="admin-ws-confirm-hint" className="text-[12px] text-muted">
            {matches ? ' ' : t('admin.deleteWorkspaceMismatch')}
          </span>
        </label>
      ) : null}

      {error ? <ErrorNote message={error} /> : null}
      {done ? (
        <p role="status" className="text-[13px] text-ink" data-testid="workspace-deleted">
          {done}
        </p>
      ) : null}

      <div>
        <Button
          variant="primary"
          size="sm"
          disabled={!matches || busy}
          onClick={() => void remove()}
          data-testid="delete-workspace"
          className="bg-signal-red border-signal-red hover:bg-signal-red-text hover:border-signal-red-text"
        >
          <Icon icon={Trash2} />
          {busy ? t('delete.busy') : t('admin.deleteWorkspaceCta')}
        </Button>
      </div>
    </section>
  )
}

// --------------------------------------------------------------------------
// Verification queue
// --------------------------------------------------------------------------
function VerificationQueue({
  items,
  error,
  onRetry,
}: {
  items: VerificationQueueItem[] | null
  error: string | null
  onRetry: () => void
}) {
  const { t } = useI18n()
  return (
    <section aria-labelledby="admin-queue" className="flex flex-col gap-4">
      <div>
        <h2 id="admin-queue" className="font-display text-[21px] font-semibold">
          {t('admin.queueTitle')}
        </h2>
        <p className="mt-1 max-w-[70ch] text-[13px] text-muted">{t('admin.queueLead')}</p>
      </div>
      {error ? <ErrorNote message={error} onRetry={onRetry} retryLabel={t('common.retry')} /> : null}
      {items && items.length === 0 ? (
        <p className="text-[13px] text-muted" data-testid="queue-empty">
          {t('admin.queueEmpty')}
        </p>
      ) : null}
      <ul className="flex flex-col gap-3" data-testid="verification-queue">
        {(items ?? []).map((item) => (
          <li key={`${item.workspace_id}:${item.pack.pack_id}`}>
            <Card>
              <CardHeader>
                <CardTitle>
                  {item.pack.gemeinde}{' '}
                  <span className="text-[13px] font-normal text-muted">
                    · {item.workspace_name ?? item.workspace_id}
                  </span>
                </CardTitle>
                <CardDescription>
                  {item.refused
                    ? t('admin.queueRefused')
                    : t('admin.queueOpen', { zones: item.open_zones, rules: item.open_rules })}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                <Badge variant={item.pack.mode === 'verified' ? 'outline' : 'indicative'}>
                  {item.pack.mode === 'verified'
                    ? t('rulebase.verifiedBadge')
                    : t('rulebase.draftBadge')}
                </Badge>
                {item.refused ? null : (
                  <Link
                    to={buildPath('rulebasePack', {
                      workspaceId: item.workspace_id,
                      kanton: item.pack.kanton.toLowerCase(),
                      gemeinde: item.pack.slug,
                    })}
                    className="inline-flex items-center gap-2 text-[13px] underline underline-offset-2"
                  >
                    <Icon icon={ShieldCheck} />
                    {t('admin.queueVerify')}
                  </Link>
                )}
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
    </section>
  )
}

// --------------------------------------------------------------------------
// Runs
// --------------------------------------------------------------------------
function RunTable({
  rows,
  error,
  onRetry,
}: {
  rows: AdminRunRow[] | null
  error: string | null
  onRetry: () => void
}) {
  const { t } = useI18n()
  return (
    <section aria-labelledby="admin-runs" className="flex flex-col gap-4">
      <div>
        <h2 id="admin-runs" className="font-display text-[21px] font-semibold">
          {t('admin.runsTitle')}
        </h2>
        <p className="mt-1 text-[13px] text-muted">{t('admin.runsCaption')}</p>
      </div>
      {error ? <ErrorNote message={error} onRetry={onRetry} retryLabel={t('common.retry')} /> : null}
      {rows && rows.length === 0 ? (
        <p className="text-[13px] text-muted">{t('admin.runsEmpty')}</p>
      ) : null}
      {rows && rows.length > 0 ? (
        <div className="overflow-x-auto" tabIndex={0} role="group" aria-label={t('admin.runsCaption')}>
          <table
            className="w-full min-w-[820px] border-collapse text-left text-[13px]"
            data-testid="admin-runs"
          >
            <caption className="sr-only">{t('admin.runsCaption')}</caption>
            <thead>
              <tr className="border-b border-border text-[12px] tracking-wide text-muted uppercase">
                <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.started')}</th>
                <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.workspace')}</th>
                <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.status')}</th>
                <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.mode')}</th>
                <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.duration')}</th>
                <th scope="col" className="py-2 pe-4 font-medium">{t('admin.col.tokens')}</th>
                <th scope="col" className="py-2 font-medium">{t('admin.openRun')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.run_id} className="border-b border-border">
                  <td className="num py-2 pe-4 whitespace-nowrap text-muted">
                    {formatMoment(row.created_at)}
                  </td>
                  <td className="py-2 pe-4">{row.workspace_name ?? row.workspace_id}</td>
                  <td className="py-2 pe-4">{t(`run.status.${row.status}` as TranslationKey)}</td>
                  <td className="py-2 pe-4 text-muted">{row.mode}</td>
                  <td className="num py-2 pe-4 whitespace-nowrap">
                    {row.duration_ms === null || row.duration_ms === undefined
                      ? '—'
                      : `${formatNumber(Math.round(row.duration_ms))} ms`}
                  </td>
                  <td className="num py-2 pe-4 whitespace-nowrap">
                    {row.cost
                      ? `${formatNumber(row.cost.input_tokens)} / ${formatNumber(row.cost.output_tokens)}`
                      : null}
                  </td>
                  <td className="py-2">
                    <Link
                      to={buildPath('run', { runId: row.run_id })}
                      className="underline underline-offset-2"
                    >
                      {row.run_id}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}
